import { useEffect } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import type { MeResponse } from "@family/shared";
import { usePreviewRole } from "./previewRoleStore";
import { writeCachedBranding } from "../lib/brandingCache";
import { resolveMembership } from "./useFamilyMembership";

/**
 * Fetches the current user + memberships. If a preview role is active
 * (admin-only feature), the first membership's role is replaced with the
 * preview role so all UI downstream sees the lower permission level.
 *
 * Side effect: caches the resolved membership's logo URLs in localStorage
 * so the Logo component can paint the right image on the very first frame
 * of the next page load.
 */
export function useMe(opts?: { enabled?: boolean }): UseQueryResult<MeResponse> {
  const previewRole = usePreviewRole();
  const query = useQuery<MeResponse>({
    queryKey: ["me"],
    queryFn: () => apiFetch<MeResponse>("/me"),
    // Callers that can mount while signed out (e.g. RequireAuth) disable
    // the query so we never fire /me without a token.
    enabled: opts?.enabled ?? true,
  });

  useEffect(() => {
    const m = resolveMembership(query.data);
    if (m) {
      writeCachedBranding(m.familySlug, {
        logoLightUrl: m.logoLightUrl,
        logoDarkUrl: m.logoDarkUrl,
      });
    }
  }, [query.data]);

  if (!query.data || !previewRole || query.data.memberships.length === 0) {
    return query;
  }

  const [first, ...rest] = query.data.memberships;
  return {
    ...query,
    data: {
      ...query.data,
      memberships: [{ ...first!, role: previewRole }, ...rest],
    },
  };
}

/**
 * Like useMe, but NEVER applies the preview-role override. Use this in the
 * Settings page to read the user's real role for the toggle UI, so an admin
 * previewing as viewer can still see + exit the preview.
 */
export function useActualMe(): UseQueryResult<MeResponse> {
  return useQuery<MeResponse>({
    queryKey: ["me"],
    queryFn: () => apiFetch<MeResponse>("/me"),
  });
}
